import moment, { Moment } from 'moment';
import type { DoesFilterPassParams } from 'ag-grid-community';
import { Array as A, Match, Option, pipe } from 'effect';

import { dateFilterModelOperator } from '@/components/AgGrid/components/AgDateFilter/dateFilterModel';
import { parseDateFilterOperator } from '@/components/AgGrid/components/AgDateFilter/operators';
import { AgDateFilterModel } from '@/components/AgGrid/components/AgDateFilter/types';

type Condition = { type?: unknown; dateFrom?: string | null; dateTo?: string | null };

type DateFilterParams = { filterValueTransform?: (value: string) => string };

const asMoment = (date?: string | null): Option.Option<Moment> =>
  pipe(
    Option.fromNullable(date),
    Option.filter((value) => value !== ''),
    Option.map((value) => moment(value)),
  );

// none: the condition sets no date, or an operator the filter does not offer, and does not filter
const evaluateCondition = (cell: Moment, condition?: Condition): Option.Option<boolean> => {
  const from = asMoment(condition?.dateFrom);
  const to = asMoment(condition?.dateTo);
  // A one-sided condition compares with whichever date is set.
  const onOrBefore = pipe(
    to,
    Option.orElse(() => from),
    Option.map((date) => cell.isSameOrBefore(date, 'day')),
  );
  const onOrAfter = pipe(
    from,
    Option.orElse(() => to),
    Option.map((date) => cell.isSameOrAfter(date, 'day')),
  );

  return pipe(
    parseDateFilterOperator(condition?.type),
    Option.flatMap((operator) =>
      Match.value(operator).pipe(
        Match.when('beforeOrOn', () => onOrBefore),
        Match.when('afterOrOn', () => onOrAfter),
        Match.when('inRange', () =>
          pipe(
            Option.all([from, to]),
            Option.map(([start, end]) =>
              cell.isBetween(start.clone().startOf('day'), end.clone().endOf('day'), null, '[]'),
            ),
            Option.orElse(() => (Option.isSome(from) ? onOrAfter : onOrBefore)),
          ),
        ),
        Match.when('withInMonth', () => Option.map(from, (date) => cell.isSame(date, 'month'))),
        Match.when('withInYear', () => Option.map(from, (date) => cell.isSame(date, 'year'))),
        Match.exhaustive,
      ),
    ),
  );
};

/**
 * The row test of the custom date filter on client-side grids. Filter handlers are on for every grid, so the grid
 * keeps the model and asks the column — `filter: { component: 'customDateFilter', doesFilterPass: doesDateFilterPass }`
 * — rather than the filter component. Reads the column's field and the filter's `filterValueTransform` from the
 * handler params.
 */
export const doesDateFilterPass = (
  params: DoesFilterPassParams<Record<string, unknown>, unknown, AgDateFilterModel, DateFilterParams>,
): boolean => {
  const { model, data, handlerParams } = params;
  if (!model) return true;

  const field = handlerParams.colDef.field;
  const rawValue = field ? data?.[field] : null;
  if (!rawValue || typeof rawValue !== 'string') return false;

  const transform = handlerParams.filterParams?.filterValueTransform;
  const cell = moment(transform ? transform(rawValue) : rawValue);
  if (!cell.isValid()) return false;

  const [primary, secondary] = 'conditions' in model ? model.conditions : [model];
  // Conditions that set no date are left out; with none left, every row passes.
  const results = A.getSomes([evaluateCondition(cell, primary), evaluateCondition(cell, secondary)]);
  if (!A.isNonEmptyArray(results)) return true;

  return dateFilterModelOperator(model) === 'and' ? A.every(results, Boolean) : A.some(results, Boolean);
};
